import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import toast, { Toaster } from 'react-hot-toast';
import { FiCopy, FiExternalLink, FiHeart } from 'react-icons/fi';
import { FaDiscord, FaShoppingCart, FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiLitecoin, SiTether } from 'react-icons/si';

function Home() {
  const cryptoWallets = [
    { 
      name: 'BITCOIN', 
      symbol: <FaBitcoin className="text-[#F7931A]" />,
      address: 'bc1qrhdcy3vl2qkjjq3ufc0u70vtun4f7yfavcl6x9', 
      uri: 'bitcoin:bc1qrhdcy3vl2qkjjq3ufc0u70vtun4f7yfavcl6x9' 
    },
    { 
      name: 'LITECOIN', 
      symbol: <SiLitecoin className="text-[#345D9D]" />,
      address: 'LaGrw1RgSWnhxKo1SiG9D9n4tVTVJyL2VC', 
      uri: 'litecoin:LaGrw1RgSWnhxKo1SiG9D9n4tVTVJyL2VC' 
    },
    { 
      name: 'USDT (ERC20)', 
      symbol: <SiTether className="text-[#26A17B]" />,
      address: '0x2cc4363765AFBe8dFE787754Ecc648A8fc4316F3', 
      uri: 'usdt:0x2cc4363765AFBe8dFE787754Ecc648A8fc4316F3' 
    },
    { 
      name: 'ETHEREUM', 
      symbol: <FaEthereum className="text-[#627EEA]" />,
      address: '0x2cc4363765AFBe8dFE787754Ecc648A8fc4316F3', 
      uri: 'ethereum:0x2cc4363765AFBe8dFE787754Ecc648A8fc4316F3' 
    },
  ];

  const notify = (walletName) => {
    toast.success(`${walletName} copied to clipboard`, {
      duration: 3000,
      position: 'bottom-center',
      style: {
        background: '#18181b', // zinc-950
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
        fontSize: '14px',
        fontWeight: '500',
      },
      iconTheme: {
        primary: '#a855f7', // purple-500
        secondary: '#fff',
      },
    });
  };

  return (
    <div className="relative min-h-screen w-full bg-slate-950 text-slate-200 selection:bg-pink-500/30 selection:text-pink-200 font-sans overflow-x-hidden">
      
      {/* Ambient Background Glows - Reduced visual noise, increased depth */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] bg-indigo-600/20 rounded-full blur-[100px] opacity-40 mix-blend-screen animate-pulse duration-[10000ms]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40rem] h-[40rem] bg-pink-600/10 rounded-full blur-[120px] opacity-40 mix-blend-screen"></div>
        <div className="absolute top-[20%] left-[50%] -translate-x-1/2 w-[50rem] h-[50rem] bg-purple-900/10 rounded-full blur-[100px] opacity-30"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center min-h-screen px-4 py-16 md:py-24">
        
        {/* Header Section */}
        <header className="w-full max-w-4xl text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-indigo-300 backdrop-blur-md mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
            </span>
            Community Supported
          </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-white leading-[1.1]">
            Help Us <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Keep Building</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Your support fuels development. Instead of a simple donation, consider <span className="text-white font-medium">grabbing something cool</span> from the store to get value in return.
          </p>
        </header>

        <main className="w-full max-w-6xl flex flex-col items-center gap-16">
          
          {/* PRIMARY CTA: The Store (Hero Card) */}
          <div className="w-full max-w-3xl group relative">
            {/* Animated Gradient Border Effect */}
            <div className="absolute -inset-[2px] rounded-3xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 opacity-75 blur-sm group-hover:opacity-100 transition duration-500 group-hover:blur-md"></div>
            
            <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-[22px] p-8 md:p-12 border border-white/10 shadow-2xl overflow-hidden">
              
              {/* Decorative background pattern inside card */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>

              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 relative z-10">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform duration-500">
                     <FaShoppingCart className="text-5xl md:text-6xl text-pink-500 drop-shadow-[0_0_15px_rgba(236,72,153,0.5)]" />
                  </div>
                </div>

                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-3xl font-bold text-white mb-3">Premium Store Access</h2>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    Unlock full access to the <strong>Sentralia (Request Management Portal)</strong> and other premium tools. It's the best way to support the project while upgrading your workflow.
                  </p>
                  
                  <a
                    href="https://store.notreal003.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-slate-950 px-8 py-3.5 rounded-xl font-bold text-lg hover:bg-indigo-50 hover:scale-[1.02] transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)] group-hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]"
                  >
                    <FaShoppingCart className="text-lg" />
                    Visit Store
                    <FiExternalLink className="text-sm opacity-50" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Elegant Divider */}
          <div className="w-full max-w-4xl flex items-center gap-4 opacity-50">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-600 to-slate-600"></div>
            <span className="text-xs font-bold tracking-[0.2em] text-slate-500 uppercase">Or Support via Crypto</span>
            <div className="h-px flex-1 bg-gradient-to-r from-slate-600 via-slate-600 to-transparent"></div>
          </div>

          {/* Secondary Options: Crypto Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            {cryptoWallets.map((wallet) => (
              <div
                key={wallet.name}
                className="group relative bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:bg-slate-800/60 hover:border-white/10 transition-all duration-300 flex flex-col items-center text-center"
              >
                {/* Coin Header */}
                <div className="flex items-center gap-3 mb-6 w-full justify-center">
                  <div className="p-2 bg-slate-800 rounded-lg shadow-sm group-hover:shadow-md transition-all">
                    <div className="text-2xl">{wallet.symbol}</div>
                  </div>
                  <h3 className="text-sm font-bold tracking-wider text-slate-200">{wallet.name}</h3>
                </div>

                {/* QR Code Container */}
                <div className="p-3 bg-white rounded-xl shadow-lg mb-6 group-hover:scale-105 transition-transform duration-300">
                  <QRCodeCanvas value={wallet.uri} size={110} />
                </div>

                {/* Address Display */}
                <div className="w-full mb-5">
                  <p className="text-[10px] sm:text-xs text-slate-500 font-mono break-all bg-black/20 p-3 rounded-lg border border-white/5 group-hover:border-white/10 transition-colors select-all">
                    {wallet.address}
                  </p>
                </div>

                {/* Copy Action */}
                <CopyToClipboard text={wallet.address} onCopy={() => notify(wallet.name)}>
                  <button className="mt-auto w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-white/5 hover:bg-white/10 border border-white/5 rounded-lg text-sm font-medium text-slate-300 hover:text-white transition-all active:scale-95 group/btn">
                    <FiCopy className="group-hover/btn:scale-110 transition-transform" />
                    <span>Copy Address</span>
                  </button>
                </CopyToClipboard>
              </div>
            ))}
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="mt-24 pt-8 border-t border-white/5 w-full max-w-6xl flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-6">
            <a
              href="https://discord.gg/sqVBrMVQmp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-indigo-400 transition-colors"
            >
              <FaDiscord className="text-lg" />
              <span>Discord Community</span>
            </a>
            <a
              href="https://store.notreal003.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-pink-400 transition-colors"
            >
              <FiHeart className="text-lg" />
              <span>Support Us</span>
            </a>
          </div>
          <div className="flex items-center gap-1 opacity-75">
            <span>&copy; {new Date().getFullYear()} NotReal003.</span>
            <span className="hidden md:inline">Built for the future.</span>
          </div>
        </footer>
      </div>

      <Toaster 
        toastOptions={{
          className: 'backdrop-blur-md',
        }}
      />
    </div>
  );
}

export default Home;

